import { Button, IconButton, Box, Typography, useMediaQuery } from "@mui/material";
import { PathwayType } from "utils/types";
import contentBg from "assets/images/course.png";
import {
  ArrowIcon,
  ChannelIcon,
  CourseIcon,
  PathwayIcon,
  PlayListIcon,
  ShareIcon,
  CloseIcon,
  SaveToListIcon,
  TimeIcon,
  CertificationIcon,
  Rating,
  NewIcon,
  ContentBanner,
  RenderHTML,
} from "components";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Theme, useTheme } from "@mui/material/styles";
import withStyles from '@mui/styles/withStyles';
import { formatTime } from "utils/helpers";
import sanitizeHtml from "sanitize-html";
import { ContentSource } from "utils/graphql/Global";
import linkedInIcon from "assets/icons/icon-logo-linkedIn.svg";
import opensesameIcon from "assets/icons/icon-logo-opensesame.svg";
import linkedmobileIcon from "assets/icons/icon-bug-linkedIn.svg";
import opensesamemobileIcon from "assets/icons/icon-bug-opensesame.svg";
import { useTranslation } from "react-i18next";
import { useFocusStyles } from "hooks/focusBorder";
import portalSettingsManager from "utils/portalSettingsManager";
import { Tooltip } from "components";
import { KeyboardArrowDown, KeyboardArrowUp, List } from "@mui/icons-material";

const htmlEnhancer = (html: string) => {
  if (!html) {
    return html;
  }
  html = html.replace("<p>", "<div>");
  html = html.replace("</p>", "</div>");
  return html;
};

type PathwayContentTileProps = {
  item?: any;
  selectTile?: any;
  type: PathwayType;
  handleShareContentTile?: any;
  setConfirmRemoveCourse?: any;
  setIsOpenPlaylist: (open: boolean) => void;
  setOpenActivity: (open: boolean) => void;
  toggleIsExpanded?: (item: any, isExpanded: boolean) => void;
  isExpanded?: boolean;
  hideSeparator?: boolean;
  isChild?: boolean;
};
export const PathwayContentTile: React.FC<PathwayContentTileProps> = ({
  item,
  selectTile,
  type,
  handleShareContentTile,
  setConfirmRemoveCourse,
  setIsOpenPlaylist,
  setOpenActivity,
  toggleIsExpanded,
  isExpanded,
  hideSeparator,
  isChild,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [contentIcon, setContentIcon] = useState<any>(null);
  const [mouseEnter, setMouseEnter] = useState<boolean>(false);
  const [conIconMouseEvent, setConIconMouseEvent] = useState<boolean>(false);
  const summaryRef = React.useRef(null);
  const [showEllipsis, setShowEllipsis] = React.useState(false);
  const { t } = useTranslation();
  const summary = item.summary ?? "";
  const maxHeight = 50;
  const showTitleTooltip = (item?.name || "").length > 30;
  const navigate = useNavigate();
  const handleToggle = () => {
    toggleIsExpanded(item, !isExpanded);
  };

  useEffect(() => {
    if (summaryRef.current !== null) {
      const checkSizeandSetEllipsis = (): any => {
        const { scrollHeight } = summaryRef.current || {
          scrollHeight: 0,
        };
        setShowEllipsis(scrollHeight > maxHeight);
      };
      checkSizeandSetEllipsis();
      window.addEventListener("resize", checkSizeandSetEllipsis);
      return window.removeEventListener("resize", checkSizeandSetEllipsis);
    }
  }, [setShowEllipsis, summary, summaryRef]);

  useEffect(() => {
    switch (item.contentType.toLowerCase()) {
      case PathwayType.playlist:
        setContentIcon(
          <Tooltip title="Playlist">
            <PlayListIcon width="18px" height="14px" stroke="#000000" />
          </Tooltip>
        );
        break;
      case PathwayType.channel:
        setContentIcon(
          <Tooltip title="Channel">
            <ChannelIcon width="18px" height="14px" stroke="#000000" />
          </Tooltip>
        );
        break;
      case PathwayType.pathway:
        setContentIcon(
          <Tooltip title="Pathway">
            <PathwayIcon width="18px" height="14px" stroke="#000000" />
          </Tooltip>
        );
        break;
      default:
        setContentIcon(
          <Tooltip title="Course">
            <CourseIcon width="18px" height="14px" stroke="#000000" />
          </Tooltip>
        );
        break;
    }
  }, [item.contentType]);

  const conIconMouseEnter = () => {
    setConIconMouseEvent(true);
  };

  const conIconMouseLeave = () => {
    setConIconMouseEvent(false);
  };

  const goToCourseDetails = () => {
    navigate(`/${item.contentType}/${item.id}`);
  };

  const focusClass = useFocusStyles();

  const hasChildren =
    item.contentType.toLowerCase() === PathwayType.pathway &&
    item.children &&
    item.children.length > 0;

  const footer = (
    <Box
      display="flex"
      flexWrap={isMobile ? "wrap" : "nowrap"}
      justifyContent={isMobile ? "start" : "space-between"}
      alignItems={isMobile ? "flex-start" : "flex-end"}
      height="34px"
      margin={isMobile ? "0 0 2.5rem -3rem" : undefined}
      component="ul"
      style={{ listStyle: "none" }}
    >
      {item.hasActivities && !hasChildren && (
        <li>
          {isMobile ? (
            <IconButton
              aria-label={t("aria_label_click_to_view_activities") + item.name}
              style={{
                padding: "4px",
                marginLeft: "10px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setOpenActivity(true);
                selectTile(item);
              }}
              className={`${focusClass.greyBorder} ${focusClass.focusItem}`}
              size="large">
              <Tooltip
                position="right"
                title={t("view_activities")}
                wrapperCss={{ display: "flex" }}
              >
                <List />
              </Tooltip>
            </IconButton>
          ) : (
            <Button
              style={{
                fontSize: "12px",
                color: theme.palette.primary.main,
                textTransform: "none",
                position: "relative",
                left: "-7px",
                padding: "0px 8px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setOpenActivity(true);
                selectTile(item);
              }}
              aria-label={t("aria_label_click_to_view_activities") + item.name}
              className={focusClass.focusItem}
            >
              {t("view_activities")}
            </Button>
          )}
        </li>
      )}
      {portalSettingsManager.application?.features?.allowShareContent &&
        item.contentType.toLowerCase() !== "playlist" && (
          <li>
            <IconButton
              aria-label={t("aria_label_click_to_share") + item.name}
              style={{
                padding: "4px",
                marginLeft: "10px",
              }}
              onClick={(e) => {
                e.preventDefault();
                handleShareContentTile(item);
              }}
              className={`${focusClass.greyBorder} ${focusClass.focusItem}`}
              size="large">
              <ShareIcon
                stroke="#000000"
                style={{ transform: "translate(20%, 25%)" }}
                itemName={item.name}
              />
            </IconButton>
          </li>
        )}
      {portalSettingsManager.application?.features?.allowCreatePlaylist &&
        item.contentType.toLowerCase() === "course" && (
          <li>
            <IconButton
              aria-label={t("aria_label_click_to_add_course_to_playlist", { course: item.name })}
              style={{
                padding: "4px",
                marginLeft: "10px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setIsOpenPlaylist(true);
                selectTile(item);
              }}
              className={`${focusClass.greyBorder} ${focusClass.focusItem}`}
              size="large">
              <SaveToListIcon
                fill={"#000000"}
                style={{ transform: "translate(20%, 25%)" }}
                contentType={item.contentType.toLowerCase()}
              />
            </IconButton>
          </li>
        )}
      <li>
        <IconButton
          role="link"
          aria-label={t("aria_label_go_to_course_details", { course: item.name })}
          style={{
            padding: "4px",
            marginLeft: "10px",
            border: `1px solid ${
              portalSettingsManager.application?.common?.themeColor || theme.palette.primary.main
            }`,
          }}
          className={`${focusClass.borderItem} ${focusClass.focusItem}`}
          onClick={goToCourseDetails}
          size="large">
          <ArrowIcon
            stroke={`${
              portalSettingsManager.application?.common?.themeColor || theme.palette.primary.main
            }`}
            style={{ transform: "translate(20%, 25%)" }}
            contentType={`${t("arrowicon_tooltip_title")} ${t(item.contentType.toLowerCase())}`}
          />
        </IconButton>
      </li>
    </Box>
  );
  const Summary = withStyles((theme: Theme) => ({
    root: {
      " & *": {
        fontSize: portalSettingsManager.fonts?.wideTile?.summaryFont?.size || "13px",
        fontFamily: portalSettingsManager.fonts?.wideTile?.summaryFont?.name || "inherit",
        color:
          portalSettingsManager.fonts?.wideTile?.summaryFont?.color || theme.palette.grey["500"],
        lineHeight: "19px",
        whiteSpace: "break-spaces",
      },
    },
  }))(Box);

  const mouseEvent = () => {
    setMouseEnter(!mouseEnter);
  };
  const certification = () => {
    return (
      <Tooltip open={mouseEnter} title={`${t("certified")} ${t(item.contentType.toLowerCase())}`}>
        <Box onMouseEnter={mouseEvent} onMouseLeave={mouseEvent}>
          <CertificationIcon width="22px" height="24px" stroke={theme.palette.grey["800"]} />
        </Box>
      </Tooltip>
    );
  };
  const externalCourseIcons = (type: ContentSource) => {
    const courseIcons = {
      [ContentSource.OPEN_SESAME]: !isMobile ? opensesameIcon : opensesamemobileIcon,
      [ContentSource.LINKED_IN]: !isMobile ? linkedInIcon : linkedmobileIcon,
      [ContentSource.KLP]: "",
      [ContentSource.NONE]: "",
    };

    const srcUrl = courseIcons[type];
    return srcUrl !== "" ? (
      <div
        style={{
          backgroundImage: `url(${srcUrl})`,
          backgroundPosition: "center right",
          backgroundRepeat: "no-repeat",
          marginRight: "10px",
          height: "20px",
          width: isMobile ? undefined : "150px",
        }}
      ></div>
    ) : (
      ""
    );
  };

  let toolTipText = t(item.contentType.toLowerCase());
  if (
    item &&
    item.contentType &&
    item.contentType.toUpperCase() == "COURSE" &&
    item.studyModes &&
    item.studyModes.length > 0
  ) {
    if (item.studyModes.length > 1) {
      toolTipText = t("hybrid");
    } else {
      if (item.studyModes.length == 1) {
        toolTipText = t(item.studyModes[0].toLowerCase());
      }
    }
  }

  return (
    <Box
      style={{
        backgroundColor: "transparent",
        borderBottom:
          isChild || hideSeparator ? undefined : `1px solid ${theme.palette.grey["300"]}`,
      }}
    >
      <Box display={{ xs: "none", sm: "block" }}>
        {type === PathwayType.playlist && (
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "-1.5rem",
              marginTop: "0.5rem",
              paddingRight: "0.5rem",
            }}
          >
            <IconButton
              role="button"
              aria-label={`${t("aria_label_click_to_delete")} ${t(item.name)} `}
              style={{
                border: `1px solid ${theme.palette.grey["A100"]}`,
                padding: "2px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setConfirmRemoveCourse(true);
                selectTile(item);
              }}
              className={focusClass.focusItem}
              size="large">
              <CloseIcon
                fill="#000000"
                stroke="#000000"
                style={{ transform: "translate(20%, 25%)", width: "1rem", height: "1rem" }}
              />
            </IconButton>
          </Box>
        )}
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: hasChildren ? "white" : undefined,
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={handleToggle}
              style={{
                height: "25px",
                width: "25px",
                color: "white",
                backgroundColor: "#004D40",
                marginRight: "25px",
              }}
              aria-label={
                isExpanded
                  ? t("aria_description_click_to_collapse", { name: item?.name })
                  : t("aria_description_click_to_expand", { name: item?.name })
              }
              aria-expanded={isExpanded}
            >
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
          {isChild && hideSeparator && (
            <span
              style={{
                width: 40,
                minHeight: 140,
                transform: "translateY(-50%)",
                borderLeft: "1px  solid gray",
                borderBottom: "1px  solid gray",
              }}
            />
          )}
          <Link to={`/${item.contentType}/${item.id}`} style={{ width: "100%" }}>
            <Box
              display="flex"
              width="100%"
              margin="1.5rem 0 0px 0px"
              style={{
                cursor: "default",
                paddingBottom: isChild && !hideSeparator ? "1.2rem" : undefined,
              }}
              overflow="hidden"
            >
              {isChild && !hideSeparator && (
                <span
                  style={{
                    margin: "auto",
                    width: 40,
                    borderBottom: "1px  solid gray",
                  }}
                />
              )}
              <Box
                style={{
                  backgroundImage: `url(${item.imageUrl || contentBg})`,
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  minHeight: "140px",
                  position: "relative",
                  width: "140px",
                  minWidth: "140px",
                  zIndex: "1",
                }}
              >
                <Box display="flex">{item.isNew && <NewIcon />}</Box>
                {type === PathwayType.pathway && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    width="100%"
                    style={{ position: "absolute", bottom: 0 }}
                  >
                    {item.isEnrolled && item.statusCode === "InProgress" && (
                      <Typography
                        style={{
                          background: `${
                            portalSettingsManager.statusColors?.inprogress?.back || "#005EB8"
                          }`,
                          width: "100%",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color:
                            portalSettingsManager.statusColors?.enrolled?.text ||
                            theme.palette.common.white,
                          letterSpacing: "2px",
                          padding: "5px 8px",
                        }}
                      >
                        {t("inprogress")}
                      </Typography>
                    )}
                    {item.isEnrolled && item.statusCode === "Enrolled" && (
                      <Typography
                        style={{
                          background: `${
                            portalSettingsManager.statusColors?.enrolled?.back || "#C6007E"
                          }`,
                          width: "100%",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color:
                            portalSettingsManager.statusColors?.enrolled?.text ||
                            theme.palette.common.white,
                          letterSpacing: "2px",
                          padding: "5px 8px",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("enrolled")}
                      </Typography>
                    )}
                    {item.isEnrolled && item.statusCode === "Completed" && (
                      <Typography
                        style={{
                          background: `${
                            portalSettingsManager.statusColors?.completed?.back || "#16837D"
                          }`,
                          width: "100%",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color:
                            portalSettingsManager.statusColors?.completed?.text ||
                            theme.palette.common.white,
                          letterSpacing: "2px",
                          padding: "5px 8px",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("completed")}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                flex="1"
                margin=""
                style={{
                  cursor: "pointer",
                  margin: isMobile ? "0 10px" : "-1px 0 0 20px",
                  overflow: "hidden",
                }}
              >
                <Box display="flex" style={{ height: "30px" }}>
                  <Tooltip
                    open={conIconMouseEvent}
                    title={
                      item.contentType.toUpperCase() == "COURSE"
                        ? t("course") + "-" + toolTipText
                        : toolTipText
                    }
                  >
                    <div onMouseEnter={conIconMouseEnter} onMouseLeave={conIconMouseLeave}>
                      {contentIcon}
                    </div>
                  </Tooltip>
                  <Tooltip
                    title={
                      item.contentType.toUpperCase() == "COURSE"
                        ? t("course") + "-" + toolTipText
                        : toolTipText
                    }
                  >
                    <Typography
                      style={{
                        fontSize: "10px",
                        color: theme.palette.grey["800"],
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginLeft: "5px",
                      }}
                    >
                      {item.contentType.toUpperCase() === "COURSE" ? toolTipText : toolTipText}
                    </Typography>
                  </Tooltip>
                  <Typography
                    style={{
                      flex: 1,
                      marginLeft: "9px",
                      fontStyle: "italic",
                      fontWeight: 400,
                      fontSize: "12px",
                      marginTop: "-2px",
                      textTransform: "lowercase",
                    }}
                  >
                    {item?.statusCode?.toUpperCase() === "NOTENROLLED"
                      ? ""
                      : t(item.statusCode?.toLowerCase())}{" "}
                  </Typography>
                  {/* {{item?.statusDate}} */}
                  {item?.source && externalCourseIcons(item?.source)}
                  {item?.hasCertificate && certification()}
                </Box>
                <span style={{ display: "flex", maxWidth: "850px" }}>
                  <Box
                    fontSize={portalSettingsManager.fonts?.wideTile?.titleFont?.size || "15px"}
                    style={{
                      marginTop: "0px",
                      fontFamily:
                        portalSettingsManager.fonts?.wideTile?.titleFont?.name || "inherit",
                      fontWeight: 700,
                      color:
                        portalSettingsManager.fonts?.wideTile?.titleFont?.color ||
                        theme.palette.grey["800"],
                      lineHeight: "21px",
                      maxWidth: "850px",
                      overflow: "hidden",
                      whiteSpace: isMobile ? "break-spaces" : "nowrap",
                      textOverflow: "ellipsis",
                    }}
                    component="h3"
                  >
                    {item.name}
                  </Box>
                  {showTitleTooltip && (
                    <Tooltip title={item.name || ""} tooltipStyle={{ maxWidth: "250px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          marginTop: "5px",
                          marginLeft: "-25px",
                          minWidth: "30px",
                          minHeight: "10px",
                          cursor: "pointer",
                          color: "transparent",
                          alignSelf: "flex-end",
                          backgroundColor: "transparent",
                        }}
                      >
                        {"..."}
                      </span>
                    </Tooltip>
                  )}
                </span>
                <Tooltip title={showEllipsis ? summary : ""} tooltipStyle={{ maxWidth: "250px" }}>
                  <Box
                    display="block"
                    flex="1"
                    marginTop="0"
                    style={{
                      fontSize: portalSettingsManager.fonts?.wideTile?.summaryFont?.size || "13px",
                      color:
                        portalSettingsManager.fonts?.wideTile?.summaryFont?.color ||
                        theme.palette.grey["500"],
                      fontFamily:
                        portalSettingsManager.fonts?.wideTile?.summaryFont?.name || "inherit",
                      lineHeight: "19px",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <div ref={summaryRef}>
                        <RenderHTML content={showEllipsis && summary.length > 300
                            ? `${summary.substring(0, 300)}...`
                            : summary} />
                    </div>
                  </Box>
                </Tooltip>
                <Box display="flex" alignItems="flex-end" flex="1">
                  {!!item.durationDisplay && (
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: item.durationDisplay ? "20px" : "0px",
                      }}
                    >
                      <>
                        <TimeIcon width="15px" height="15px" stroke={theme.palette.grey["800"]} />
                        <Typography
                          style={{
                            fontSize: "12px",
                            color: theme.palette.grey["800"],
                            letterSpacing: 0,
                            fontWeight: 400,
                            float: "right",
                            marginLeft: "5px",
                          }}
                        >
                          {formatTime(item.durationDisplay)}
                        </Typography>
                      </>
                    </Box>
                  )}
                  <Box
                    display={{ xs: "none", sm: "flex" }}
                    style={{ marginTop: "0px", height: "16px" }}
                  >
                    {portalSettingsManager.application?.features?.enableContentRating &&
                      type !== PathwayType.playlist && (
                        <Rating
                          score={item.rating || 0}
                          numberOfReviews={item.numberOfPeopleWhoRated || 0}
                          display={true}
                        />
                      )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Link>
        </Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: isChild
              ? !hideSeparator
                ? "-2rem 0.5rem 0.5rem 0.5rem"
                : "0rem 0.5rem 0.5rem 0.5rem"
              : "-2rem 0.5rem 0.5rem 0rem",
          }}
        >
          {footer}
        </Box>
        {isChild && !hideSeparator && (
          <hr style={{ marginLeft: 40, border: "0.5px solid lightgray" }} />
        )}
      </Box>
      <Box display={{ xs: "block", sm: "none" }}>
        {type === PathwayType.playlist && (
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "-1.5rem",
              marginTop: "0.5rem",
              paddingRight: "0.5rem",
            }}
          >
            <IconButton
              role="button"
              aria-label={`${t("aria_label_click_to_delete")} ${t(item.name)} `}
              style={{
                border: `1px solid ${theme.palette.grey["A100"]}`,
                padding: "2px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setConfirmRemoveCourse(true);
                selectTile(item);
              }}
              className={focusClass.focusItem}
              size="large">
              <CloseIcon
                fill="#000000"
                stroke="#000000"
                style={{ transform: "translate(20%, 25%)", width: "1rem", height: "1rem" }}
              />
            </IconButton>
          </Box>
        )}
        <Link to={`/${item.contentType}/${item.id}`} style={{ width: "100%", display: "block" }}>
          <Box
            margin=".5rem 0 .5rem 0"
            style={{
              cursor: "default",
            }}
          >
            {item.isNew && <Box display="flex">{<NewIcon />}</Box>}
            <Box
              flex="1"
              style={{
                cursor: "pointer",
              }}
            >
              <Box display="flex">
                <Tooltip
                  open={conIconMouseEvent}
                  title={
                    item.contentType.toUpperCase() == "COURSE"
                      ? t("course") + "-" + toolTipText
                      : toolTipText
                  }
                >
                  <div onMouseEnter={conIconMouseEnter} onMouseLeave={conIconMouseLeave}>
                    {contentIcon}
                  </div>
                </Tooltip>
                <Tooltip
                  title={
                    item.contentType.toUpperCase() == "COURSE"
                      ? t("course") + "-" + toolTipText
                      : toolTipText
                  }
                >
                  <Typography
                    style={{
                      fontSize: "10px",
                      color: theme.palette.grey["800"],
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      marginLeft: "5px",
                    }}
                  >
                    {item.contentType.toUpperCase() === "COURSE" ? toolTipText : toolTipText}
                  </Typography>
                </Tooltip>
                <Typography
                  style={{
                    flex: 1,
                    marginLeft: "9px",
                    fontStyle: "italic",
                    fontWeight: 400,
                    fontSize: "12px",
                    marginTop: "-2px",
                    textTransform: "lowercase",
                  }}
                >
                  {item?.statusCode?.toUpperCase() === "NOTENROLLED"
                    ? ""
                    : t(item.statusCode?.toLowerCase())}{" "}
                </Typography>
                {item?.source && externalCourseIcons(item?.source)}
                {item?.hasCertificate && certification()}
              </Box>
              <span style={{ display: "flex" }}>
                <Box
                  fontSize={portalSettingsManager.fonts?.wideTile?.titleFont?.size || "15px"}
                  style={{
                    marginTop: "0px",
                    fontFamily: portalSettingsManager.fonts?.wideTile?.titleFont?.name || "inherit",
                    fontWeight: 700,
                    color:
                      portalSettingsManager.fonts?.wideTile?.titleFont?.color ||
                      theme.palette.grey["800"],
                    lineHeight: "21px",
                    whiteSpace: isMobile ? "break-spaces" : "nowrap",
                  }}
                  component="h3"
                >
                  {item.name}
                </Box>
              </span>
              {type === PathwayType.pathway && (
                <ContentBanner
                  item={item}
                  contentType={item.contentType?.toLowerCase()}
                  showProgress={true}
                  isAutoEnrolled={item.isAutoEnrolled}
                />
              )}
              <Summary
                style={{
                  margin: "5px 0px 0px 0px",
                }}
              >
                <div>  
                    <RenderHTML content={htmlEnhancer(summary)} />
                  </div>
              </Summary>
              <Box display="flex" alignItems="flex-end" flex="1">
                {!!item.durationDisplay && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: item.durationDisplay ? "20px" : "0px",
                    }}
                  >
                    <>
                      <TimeIcon width="15px" height="15px" stroke={theme.palette.grey["800"]} />
                      <Typography
                        style={{
                          fontSize: "12px",
                          color: theme.palette.grey["800"],
                          letterSpacing: 0,
                          fontWeight: 400,
                          float: "right",
                          marginLeft: "5px",
                        }}
                      >
                        {formatTime(item.durationDisplay)}
                      </Typography>
                    </>
                  </Box>
                )}
                <Box
                  display={{ xs: "none", sm: "flex" }}
                  style={{ marginTop: "0px", height: "16px" }}
                >
                  {portalSettingsManager.application?.features?.enableContentRating &&
                    type !== PathwayType.playlist && (
                      <Rating
                        score={item.rating || 0}
                        numberOfReviews={item.numberOfPeopleWhoRated || 0}
                        display={true}
                      />
                    )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Link>
        {footer}
      </Box>
      {hasChildren && isExpanded && (
        <Box display={{ xs: "none", sm: "block" }} style={{ paddingLeft: 25, marginTop: -50 }}>
          <ul>
            {item.children.map((child: any, index: number) => (
              <li
                style={{
                  listStyle: "none",
                  borderLeft: index == item.children.length - 1 ? undefined : "1px solid gray",
                  marginTop: -8,
                }}
                key={index}
              >
                <PathwayContentTile
                  item={child}
                  type={type}
                  setIsOpenPlaylist={setIsOpenPlaylist}
                  setOpenActivity={setOpenActivity}
                  selectTile={selectTile}
                  handleShareContentTile={handleShareContentTile}
                  hideSeparator={index == item.children.length - 1}
                  isChild={true}
                />
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};
